import { UnprivilegedEditor } from "react-quill";
import { UploadOptions } from "@maysoft/tus-js-client";
import { FormControl, FormHelperText, FormLabel } from "@mui/material";
import React, { useState, memo, useMemo, useCallback, useRef } from "react";

import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import { Constants } from "../../constants/constants";
import { FileProcessingStatus, FileAccessMode, Mode } from "../../constants/enums";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";
import ResumableUploadService, { ResumableUploadRequest } from "../../services/asset/resumableUpload.service";

import "./quill.snow.css";
import "./quill.bubble.css";

interface IProps {
    label?: string;
    required?: boolean,
    containerClassName?: string,
    onChange?: (content?: any) => void,
    errorMessage?: string,
    renderKey?: number | undefined;
    value?: string;

    mode?: Mode;
    forceUpdateProp?: boolean;
}

const ReactQuill = React.lazy(() => import("react-quill").then(value => ({ default: value.default })))

const MAX_CONTENT_LENGTH = 1 * 1024 * 1024;

const QuillHtmlEditor: React.FC<IProps> = (props: IProps) => {
    const quillRef = useRef<any>();
    const strings = useStrings();
    const {
        serviceCode,
        tenantCode,
        onError,
        onShowLoading,
        onHideLoading
    } = useCommonComponentContext();

    const { label, required, containerClassName = "", errorMessage, value = "", onChange, mode } = props;
    const [isFocus, setIsFocus] = useState<boolean>(false);
    const [isQuillRendered, setIsQuillRendered] = useState<boolean>(false);

    const removeBase64Images = useCallback((elements: HTMLCollection) => {
        for (const element of elements) {
            const images = element.getElementsByTagName("img");
            if (images.length === 0) {
                continue;
            }
            for (const image of images) {
                if (image.src.includes("base64")) {
                    image.remove();
                }
            }
        }
    }, [])

    const handleChange = useCallback((value: string, delta: any, source: any, editor: UnprivilegedEditor) => {
        const contentLength = Helpers.stringToSizeByte(value);
        if (contentLength >= MAX_CONTENT_LENGTH) {
            onError(strings.Common.HTML_EDITOR_CONTENT_ERROR);
            const quillObject = quillRef.current.getEditor();
            removeBase64Images(quillObject.root.children);
        } else {
            onChange && onChange(value?.trim());
        }
    }, [onChange, removeBase64Images])

    const imageHandler = useCallback(async () => {
        const input = document.createElement("input");

        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.click();
        input.onchange = async () => {
            try {
                onShowLoading && onShowLoading();

                const file: File = (input && input.files) ? input.files[0] : null;
                if (Helpers.isNullOrEmpty(file)) {
                    return;
                }
                const request: ResumableUploadRequest = {
                    folderPath: Constants.CloudStorage.EDITOR_CONTENT_FOLDER,
                    processingStatus: `${FileProcessingStatus.Processed}`,
                    accessMode: `${FileAccessMode.Public}`,
                    tenantCode: tenantCode,
                    serviceCode: serviceCode
                };

                const options: UploadOptions = {
                    onSuccess: (res) => {
                        let id = "";
                        if (res.getHeader("asset-id")) {
                            id = res.getHeader("asset-id");
                        }

                        let accessUrl = "";
                        if (res.getHeader("asset-url")) {
                            accessUrl = res.getHeader("asset-url");
                        }

                        let error = "";
                        if (res.getHeader("asset-error")) {
                            error = res.getHeader("asset-error");
                            onError && onError(strings.Common.UPLOAD_IMAGE_FAILED);
                        }

                        const response = { id, accessUrl, error }
                        const quillObject = quillRef.current.getEditor();
                        const range = quillObject.getSelection();

                        quillObject.insertEmbed(range.index, "image", response.accessUrl);
                        quillObject.setSelection(quillObject.getLength(), 0);
                        quillObject.root.dispatchEvent(new KeyboardEvent("keydown", {
                            key: "Enter",
                            code: "Enter",
                            which: 13,
                            keyCode: 13,
                        }))

                        onHideLoading && onHideLoading();
                    },
                    onError: (error: any) => {
                        onError(Helpers.renderExceptionError(error));
                    },
                };

                const tusUpload = ResumableUploadService.upload(file, request, options);
                tusUpload.start();
            } catch (error) {
                onError && onError(Helpers.renderExceptionError(error));
            } 
        };
    }, [])

    const [modules, formats] = useMemo(() => {
        if (isQuillRendered && quillRef.current) {
            const quillEditor = quillRef.current.getEditor();
            const toolbar: any = quillEditor.getModule("toolbar");
            toolbar.addHandler("image", imageHandler);
        }

        const modules: any = {
            toolbar: {
                container: [
                    ["bold", "italic", "underline", "strike"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["blockquote", "code-block"],
                    ["link", "image"],
                    [{ indent: "-1" }, { indent: "+1" }],
                    [{ align: [] }],
                    [{ header: [1, 2, 3, 4, 5, 6, false] }],
                    ["clean"],
                ]
            }
        };

        const formats = [
            "header",
            "bold",
            "italic",
            "underline",
            "strike",
            "blockquote",
            "list",
            "bullet",
            "indent",
            "link",
            "image",
        ];

        return [modules, formats]
    }, [isQuillRendered, imageHandler])

    const renderEditor = useMemo(() => {
        if (quillRef.current) {
            setIsQuillRendered(true)
        }

        if (mode === Mode.View) {
            return (
                <div className="mt-2" style={{ border: "solid 1px #cccccc" }}>
                    <ReactQuill readOnly theme="bubble" value={value} />
                </div>
            )
        }

        return (
            <ReactQuill
                ref={quillRef}
                className="mt-2"
                formats={formats}
                modules={modules}
                theme={"snow"}
                onFocus={() => setIsFocus(true)}
                onBlur={() => setIsFocus(false)}
                value={value}
                onChange={handleChange}
            />
        )
    }, [handleChange, value, mode, formats, modules])

    return (
        <React.Suspense fallback={<div></div>}>
            <FormControl
                fullWidth
                className={containerClassName}
                focused={isFocus}
                error={!Helpers.isNullOrEmpty(errorMessage)}>
                <FormLabel>{required ? `${label} *` : label}</FormLabel>
                {renderEditor}
                <FormHelperText className="mx-0">{props.errorMessage}</FormHelperText>
            </FormControl>
        </React.Suspense>
    );
}

export default memo(QuillHtmlEditor);